import React, { useState, useEffect, useCallback } from 'react';
import { sheetsExportService } from '../../../api/apiService';
import '../../../App.css';
import './SheetsExportSettings.css';

/**
 * Settings for the configurable Google Sheets export (superadmin only).
 *
 * The original nightly export is untouched by this screen — it keeps its built-in spreadsheet
 * and runs as it always has. Everything configured here is a SECOND export that runs on the
 * same nightly schedule and writes to the spreadsheets listed below.
 *
 * The page deliberately spells out what the export does. All three of these surprise people:
 * it clears the tab before writing, it covers this year and last year, and it runs at night.
 */
function SheetsExportSettings() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [targets, setTargets] = useState([]);

  // Credential form
  const [keyJson, setKeyJson] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [keyMessage, setKeyMessage] = useState(null);
  const [copied, setCopied] = useState(false);

  // Target form — one form, reused for "new" and "edit"
  const [form, setForm] = useState(null);
  const [savingTarget, setSavingTarget] = useState(false);

  // Per-target action state, keyed by id
  const [busy, setBusy] = useState({});
  const [results, setResults] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await sheetsExportService.getSettings();
      setCredentials(res.data?.credentials || null);
      setTargets(res.data?.targets || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load export settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveKey = async () => {
    if (!keyJson.trim()) return;
    setSavingKey(true);
    setKeyMessage(null);
    try {
      const res = await sheetsExportService.saveCredentials(keyJson.trim());
      setKeyMessage({ ok: true, text: res.data?.message || 'Key stored.' });
      setKeyJson('');
      load();
    } catch (err) {
      setKeyMessage({ ok: false, text: err.response?.data?.message || err.message || 'Failed to store the key' });
    } finally {
      setSavingKey(false);
    }
  };

  const handleDeleteKey = async () => {
    if (!window.confirm('Remove the stored Google key? Exports stop working until a new key is added (unless the server still has one in its environment).')) return;
    try {
      const res = await sheetsExportService.deleteCredentials();
      setKeyMessage({ ok: true, text: res.data?.message || 'Key removed.' });
      load();
    } catch (err) {
      setKeyMessage({ ok: false, text: err.response?.data?.message || err.message || 'Failed to remove the key' });
    }
  };

  const copyEmail = () => {
    if (!credentials?.client_email) return;
    navigator.clipboard?.writeText(credentials.client_email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const newTarget = () => setForm({ name: '', spreadsheet_id: '', tab_name: 'Sheet2', years_back: '1', enabled: true });
  const editTarget = (t) => setForm({
    id: t.id,
    name: t.name,
    spreadsheet_id: t.spreadsheet_id,
    tab_name: t.tab_name,
    years_back: t.years_back === null || t.years_back === undefined ? 'all' : String(t.years_back),
    enabled: !!t.enabled,
  });

  const handleSaveTarget = async () => {
    if (!form?.name?.trim() || !form?.spreadsheet_id?.trim()) {
      setError('A name and a spreadsheet id (or URL) are required');
      return;
    }
    setSavingTarget(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        spreadsheet_id: form.spreadsheet_id.trim(),
        tab_name: form.tab_name.trim() || 'Sheet2',
        // 'all' is the explicit "no date limit" choice; everything else is a year count
        years_back: form.years_back === 'all' ? null : Number(form.years_back),
        enabled: !!form.enabled,
      };
      if (form.id) await sheetsExportService.updateTarget(form.id, payload);
      else await sheetsExportService.createTarget(payload);
      setForm(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save the target');
    } finally {
      setSavingTarget(false);
    }
  };

  const handleDeleteTarget = async (t) => {
    if (!window.confirm(`Delete the target "${t.name}"? The spreadsheet itself is not touched.`)) return;
    try {
      await sheetsExportService.deleteTarget(t.id);
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete the target');
    }
  };

  /**
   * Test and Run answer 200 even on failure — the real outcome is `ok` in the body, so read
   * that rather than relying on the request not throwing.
   */
  const handleTest = async (t) => {
    setBusy((b) => ({ ...b, [t.id]: 'test' }));
    setResults((r) => ({ ...r, [t.id]: null }));
    try {
      const res = await sheetsExportService.testTarget(t.id);
      const d = res.data || {};
      setResults((r) => ({ ...r, [t.id]: { ok: !!d.ok, text: d.message, tabs: d.tabs, title: d.spreadsheetTitle } }));
    } catch (err) {
      setResults((r) => ({ ...r, [t.id]: { ok: false, text: err.response?.data?.message || err.message || 'Test failed' } }));
    } finally {
      setBusy((b) => ({ ...b, [t.id]: null }));
    }
  };

  const handleRun = async (t) => {
    if (!window.confirm(`Run "${t.name}" now?\n\nThis CLEARS the tab "${t.tab_name}" and rewrites it. Anything else in that tab is lost.`)) return;
    setBusy((b) => ({ ...b, [t.id]: 'run' }));
    setResults((r) => ({ ...r, [t.id]: null }));
    try {
      const res = await sheetsExportService.runTarget(t.id);
      const d = res.data || {};
      setResults((r) => ({ ...r, [t.id]: { ok: !!d.ok, text: d.message } }));
      load(); // pick up the new last-run values
    } catch (err) {
      setResults((r) => ({ ...r, [t.id]: { ok: false, text: err.response?.data?.message || err.message || 'Run failed' } }));
    } finally {
      setBusy((b) => ({ ...b, [t.id]: null }));
    }
  };

  const formatDateTime = (d) => {
    if (!d) return 'never';
    const date = new Date(d);
    return isNaN(date.getTime()) ? String(d) : date.toLocaleString('de-DE');
  };

  const describeWindow = (yearsBack) => {
    if (yearsBack === null || yearsBack === undefined) return 'every booking';
    const y = new Date().getFullYear();
    const n = Number(yearsBack);
    if (n === 0) return `${y} only`;
    return `${y - n}–${y}`;
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper"><h1>Google Sheets Export</h1><p>Loading…</p></div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Google Sheets Export</h1>

        <div className="sheets-export-intro">
          <p>
            Bookings are exported to the spreadsheets configured below, every night at
            <strong> 2:10 AM</strong> (Berlin). Each run <strong>clears the tab and rewrites
            it</strong> — never add anything by hand to a tab used here, it would be deleted on
            the next run. By default a target covers <strong>this year and last year</strong>
            {' '}(currently {describeWindow(1)}), based on each booking's start date.
          </p>
          <p className="sheets-export-note">
            The older export to the built-in spreadsheet is unaffected by this screen and keeps
            running as before.
          </p>
        </div>

        {error && <div className="sheets-export-message error">{error}</div>}

        {/* ── Google setup ────────────────────────────────────────────────────── */}
        <h2>1. Google access</h2>
        <ol className="sheets-export-checklist">
          <li>In Google Cloud, create a project and <strong>enable the Google Sheets API</strong>.</li>
          <li>Create a <strong>service account</strong>, then Keys → Add key → JSON.</li>
          <li>Paste that JSON file's contents below.</li>
          <li><strong>Share every spreadsheet</strong> with the service account's email address, as <strong>Editor</strong>.</li>
          <li>Each spreadsheet must already contain the tab you name (default <code>Sheet2</code>) — the export does not create tabs.</li>
        </ol>

        <div className="sheets-export-card">
          {credentials?.configured ? (
            <>
              <div className="sheets-export-row">
                <span className="sheets-export-label">Status</span>
                <span>
                  {credentials.source === 'env'
                    ? 'Using the key from the server environment (no key stored here yet)'
                    : 'Key stored'}
                </span>
              </div>
              {credentials.client_email && (
                <div className="sheets-export-row">
                  <span className="sheets-export-label">Share sheets with</span>
                  <span>
                    <code className="sheets-export-email">{credentials.client_email}</code>
                    <button type="button" className="cn-btn cn-btn-secondary" onClick={copyEmail}>
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </span>
                </div>
              )}
              {credentials.project_id && (
                <div className="sheets-export-row">
                  <span className="sheets-export-label">Project</span>
                  <span>{credentials.project_id}</span>
                </div>
              )}
              {credentials.source === 'database' && (
                <div className="sheets-export-row">
                  <span className="sheets-export-label">Last updated</span>
                  <span>{formatDateTime(credentials.updated_at)}{credentials.updated_by ? ` by ${credentials.updated_by}` : ''}</span>
                </div>
              )}
            </>
          ) : (
            <p className="sheets-export-warning">
              No Google key configured — nothing can be exported until one is added.
            </p>
          )}

          <textarea
            className="sheets-export-textarea"
            placeholder={'Paste the service account JSON here, e.g.\n{\n  "type": "service_account",\n  "project_id": "...",\n  "private_key": "-----BEGIN PRIVATE KEY-----\\n...",\n  "client_email": "...@....iam.gserviceaccount.com"\n}'}
            value={keyJson}
            onChange={(e) => setKeyJson(e.target.value)}
            rows={6}
          />
          <div className="sheets-export-actions">
            <button type="button" className="cn-btn cn-btn-primary" onClick={handleSaveKey} disabled={savingKey || !keyJson.trim()}>
              {savingKey ? 'Storing…' : (credentials?.source === 'database' ? 'Replace key' : 'Store key')}
            </button>
            {credentials?.source === 'database' && (
              <button type="button" className="cn-btn cn-btn-secondary" onClick={handleDeleteKey}>
                Remove stored key
              </button>
            )}
            <span className="sheets-export-note">The key is encrypted before it is saved and is never shown again.</span>
          </div>
          {keyMessage && (
            <div className={`sheets-export-message ${keyMessage.ok ? 'success' : 'error'}`}>{keyMessage.text}</div>
          )}
        </div>

        {/* ── Targets ─────────────────────────────────────────────────────────── */}
        <h2>2. Spreadsheets to export to</h2>

        {targets.length === 0 && !form && (
          <p className="sheets-export-note">
            Nothing is configured, so this export does nothing. Add a target to switch it on.
          </p>
        )}

        {targets.map((t) => {
          const result = results[t.id];
          return (
            <div key={t.id} className="sheets-export-card">
              <div className="sheets-export-target-head">
                <div>
                  <strong>{t.name}</strong>
                  {!t.enabled && <span className="sheets-export-badge off">disabled</span>}
                  {t.enabled && <span className="sheets-export-badge on">nightly</span>}
                </div>
                <div className="sheets-export-actions">
                  <button type="button" className="cn-btn cn-btn-secondary" onClick={() => handleTest(t)} disabled={!!busy[t.id]}
                    title="Checks access and reports the tabs found. Writes nothing.">
                    {busy[t.id] === 'test' ? 'Testing…' : 'Test'}
                  </button>
                  <button type="button" className="cn-btn cn-btn-secondary" onClick={() => handleRun(t)} disabled={!!busy[t.id]}
                    title="Clears the tab and rewrites it now">
                    {busy[t.id] === 'run' ? 'Running…' : 'Run now'}
                  </button>
                  <button type="button" className="cn-btn cn-btn-secondary" onClick={() => editTarget(t)} disabled={!!busy[t.id]}>Edit</button>
                  <button type="button" className="cn-btn cn-btn-secondary" onClick={() => handleDeleteTarget(t)} disabled={!!busy[t.id]}>Delete</button>
                </div>
              </div>

              <div className="sheets-export-row">
                <span className="sheets-export-label">Spreadsheet</span>
                <span>
                  <code>{t.spreadsheet_id}</code>{' '}
                  <a href={`https://docs.google.com/spreadsheets/d/${t.spreadsheet_id}/edit`} target="_blank" rel="noreferrer">open</a>
                </span>
              </div>
              <div className="sheets-export-row">
                <span className="sheets-export-label">Tab</span>
                <span><code>{t.tab_name}</code> — cleared and rewritten on every run</span>
              </div>
              <div className="sheets-export-row">
                <span className="sheets-export-label">Covers</span>
                <span>{describeWindow(t.years_back)}</span>
              </div>
              <div className="sheets-export-row">
                <span className="sheets-export-label">Last run</span>
                <span className={t.last_run_status === 'error' ? 'sheets-export-failed' : undefined}>
                  {t.last_run_status === 'error' && <strong>FAILED · </strong>}
                  {formatDateTime(t.last_run_at)}
                  {t.last_run_rows != null && t.last_run_status === 'success' ? ` · ${t.last_run_rows} rows` : ''}
                  {t.last_run_message ? ` · ${t.last_run_message}` : ''}
                </span>
              </div>

              {result && (
                <div className={`sheets-export-message ${result.ok ? 'success' : 'error'}`}>
                  {result.text}
                  {result.tabs && result.tabs.length > 0 && (
                    <div className="sheets-export-note">Tabs in that spreadsheet: {result.tabs.join(', ')}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {form ? (
          <div className="sheets-export-card">
            <h3>{form.id ? 'Edit target' : 'New target'}</h3>
            <label className="sheets-export-field">
              <span>Name</span>
              <input className="inputtext" type="text" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Controlling – bookings" />
            </label>
            <label className="sheets-export-field">
              <span>Spreadsheet id or URL</span>
              <input className="inputtext" type="text" value={form.spreadsheet_id}
                onChange={(e) => setForm({ ...form, spreadsheet_id: e.target.value })}
                placeholder="https://docs.google.com/spreadsheets/d/… (the id is taken from the link)" />
            </label>
            <label className="sheets-export-field">
              <span>Tab name</span>
              <input className="inputtext" type="text" value={form.tab_name}
                onChange={(e) => setForm({ ...form, tab_name: e.target.value })}
                placeholder="Sheet2" />
            </label>
            <label className="sheets-export-field">
              <span>Time range</span>
              <select className="inputtext" value={form.years_back}
                onChange={(e) => setForm({ ...form, years_back: e.target.value })}>
                <option value="1">This year and last year (default)</option>
                <option value="0">This year only</option>
                <option value="2">This year and the two before</option>
                <option value="4">Last five years</option>
                <option value="all">Every booking ever</option>
              </select>
            </label>
            <label className="sheets-export-checkbox">
              <input type="checkbox" checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              <span>Include in the nightly run</span>
            </label>
            <div className="sheets-export-actions">
              <button type="button" className="cn-btn cn-btn-primary" onClick={handleSaveTarget} disabled={savingTarget}>
                {savingTarget ? 'Saving…' : 'Save target'}
              </button>
              <button type="button" className="cn-btn cn-btn-secondary" onClick={() => setForm(null)} disabled={savingTarget}>
                Cancel
              </button>
              <span className="sheets-export-note">
                After saving, press <strong>Test</strong> — it checks access without writing anything.
              </span>
            </div>
          </div>
        ) : (
          <button type="button" className="cn-btn cn-btn-primary" onClick={newTarget}>Add spreadsheet</button>
        )}
      </div>
    </div>
  );
}

export default SheetsExportSettings;
